import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  Avatar,
  Stack,
  LinearProgress,
  Chip,
  Button,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import useDocData from "../../../Hooks/useDocData";
import LaunchIcon from "@mui/icons-material/Launch";
import { Helmet } from "react-helmet-async";


/* ---------------- PAGE ---------------- */
const ArtistAlley = () => {
  const [ourArtists, setOurArtists] = useState([]);
  const mainData = useDocData();
  const artists = useMemo(() => {
    const list = mainData[0] || [];
    return [...list].sort((a, b) => {
      const aId = typeof a?.doc_id === "number" ? a.doc_id : -1;
      const bId = typeof b?.doc_id === "number" ? b.doc_id : -1;
      return bId - aId;
    });
  }, [mainData]);
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "What is Artist Alley?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Artist Alley is a year-round showcase of local and independent artists selling original art, prints, stickers, and collectibles at Den Den Claw.",
        },
      },
      {
        "@type": "Question",
        name: "Is it year-round?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes, Artist Alley is open year-round during Den Den Claw business hours.",
        },
      },
      {
        "@type": "Question",
        name: "Is it free to browse?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes, browsing Artist Alley is free.",
        },
      },
      {
        "@type": "Question",
        name: "Where is it located?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Artist Alley is located inside Den Den Claw in Ottawa's Chinatown.",
        },
      },
    ],
  };

  useEffect(() => {
    if (artists.length > 0) {
      setOurArtists(artists);
    }
  }, [artists]);
  const buildArtistPath = (artist) =>
    `/explore/artist-alley/${encodeURIComponent(
      artist?.username || artist?.name || ""
    )}`;
  const getOptimizedImage = (src, size) => {
    if (!src) return "";
    if (src.includes("res.cloudinary.com") && src.includes("/upload/")) {
      const transformation = `f_auto,q_auto,w_${size},h_${size},c_fill`;
      return src.replace("/upload/", `/upload/${transformation}/`);
    }
    return src;
  };

  return (
    <>

          <Helmet>
        <title>Artist Alley Ottawa | Local Artists at Den Den Claw</title>
        <meta
          name="description"
          content="Discover Artist Alley in Ottawa at Den Den Claw. Shop original art, prints, stickers, and merch from local and independent artists year-round."
        />
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <link rel="canonical" href="https://www.dendenclaw.com/explore/artist-alley" />
      </Helmet>
    <Box
      sx={{
        py: 10,
        background: "linear-gradient(180deg, #e8f2ff 0%, #f5faff 100%)",
      }}
    >
      <Container maxWidth="xl">
        {/* ---------- HEADER ---------- */}
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <Typography
            variant="h3"
            component="h1"
            sx={{ fontWeight: 700, mb: 2 }}
          >
            Artist Alley in Ottawa - Local Artists at Den Den Claw
          </Typography>
          {/* 👇 KEY SEO LINE */}
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: "primary.main",
              mb: 2,
            }}
          >
            A convention-style Artist Alley in Ottawa — open year-round
          </Typography>

          <Typography
            variant="subtitle1"
            sx={{ maxWidth: 780, mx: "auto", color: "text.secondary" }}
          >
           Discover local Ottawa artists at our year-round Artist Alley in Ottawa, featuring independent creators selling original art, prints, stickers, keychains, and collectibles — just like you’d find at conventions, but available any time inside Den Den Claw in Chinatown.
          </Typography>

                   <Button
              variant="contained"
              size="large"
              component={Link}
              to="/contact"
              sx={{
                borderRadius: 999,
                px: 4,
                textTransform: "none",
                fontWeight: 600,
                mt: 4
              }}
            >
              Visit Us
            </Button>
        </Box>
          <Box
  sx={{
    mb: 8,
    p: 4,
    borderRadius: 3,
    backgroundColor: "background.paper",
    boxShadow: 6,
    textAlign: "center",
  }}
>
  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
    Interested in Artist Alley?
  </Typography>

  <Typography sx={{ mb: 3, color: "text.secondary" }}>
    For Artist Alley FAQs, availability, and announcements,
    visit the Artist Alley manager portal.
  </Typography>
  <Stack
    direction={{ xs: "column", sm: "row" }}
    spacing={2}
    justifyContent="center"
  >

    <Button
      variant="contained"
      startIcon={<LaunchIcon />}
      href="https://artist-alley-manager.vercel.app/"
      target="_blank"
      sx={{
        borderRadius: 999,
        px: 3,
        textTransform: "none",
        fontWeight: 600,
      }}
    >
      Visit Artist Alley Portal
    </Button>

  </Stack>
     <Typography variant="caption" sx={{ display: "block", mt: 2 }}>
  (Rental terms, pricing, timelines, and availability live here!)
</Typography>
</Box>

        {/* ---------- LOADING ---------- */}
        {artists.length === 0 && (
          <Box sx={{ width: "100%", mt: 6 }}>
            <LinearProgress color="secondary" />
          </Box>
        )}

        {/* ---------- ARTIST GALLERY ---------- */}
        <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
          {ourArtists.map((artist, index) => (
            <Grid
              key={artist.id || artist.username || artist.doc_id}
              item
              xs={6}
              sm={4}
              md={4}
              lg={3}
              sx={{ display: "flex", justifyContent: "center" }}
            >
              <ArtistCard
                artist={artist}
                to={buildArtistPath(artist)}
                priority={index < 4}
                getOptimizedImage={getOptimizedImage}
              />
            </Grid>
          ))}
        </Grid>

      </Container>
    </Box>
    </>
  );
};

/* ---------------- ARTIST CARD ---------------- */
const ArtistCard = ({ artist, to, priority = false, getOptimizedImage }) => {
  const rawSrc = artist.avatarUrl || artist.doc_img || "";
  const fallbackSrc = artist.doc_img || "";
  const initialSrc = getOptimizedImage ? getOptimizedImage(rawSrc, 440) : rawSrc;
  const [currentSrc, setCurrentSrc] = useState(initialSrc);

  useEffect(() => {
    setCurrentSrc(initialSrc);
  }, [initialSrc]);

  const handleImageError = () => {
    if (currentSrc && fallbackSrc && currentSrc !== fallbackSrc) {
      const nextSrc = getOptimizedImage
        ? getOptimizedImage(fallbackSrc, 440)
        : fallbackSrc;
      setCurrentSrc(nextSrc);
      return;
    }
    setCurrentSrc("");
  };

  return (
    <Card
      sx={{
        width: "100%",
        maxWidth: { xs: "100%", md: 280 },
        borderRadius: 3,
        boxShadow: 8,
        position: "relative",
        overflow: "hidden",
        transition: "0.3s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: 10,
        },
        "&:hover .ig-overlay": { opacity: 1 },
        "&:hover .artist-avatar": { transform: "scale(1.05)" },
      }}
    >
      {/* BADGES */}
      {artist.featured && (
        <Chip
          label="Recently Featured ✨"
          color="primary"
          size="small"
          sx={{
            position: "absolute",
            top: 12,
            left: 12,
            zIndex: 3,
            fontWeight: 600,
          }}
        />
      )}

      {artist.comingSoon && (
        <Chip
          label="Coming Soon ✨"
          color="primary"
          size="small"
          sx={{
            position: "absolute",
            top: 12,
            left: 12,
            zIndex: 3,
            fontWeight: 600,
          }}
        />
      )}

      {/* ENTIRE CARD IS CLICKABLE */}
      <CardActionArea
        component={Link}
        to={to}
        disableRipple
        disableTouchRipple
        sx={{
          height: "100%",
          borderRadius: 3,
          "& .MuiCardActionArea-focusHighlight": {
            opacity: 0, // removes MUI hover tint entirely
          },
        }}
      >
        {/* IMAGE SECTION */}
        <Box sx={{ pt: { xs: 0, md: 3 }, pb: { xs: 0, md: 1 } }}>
          <Box
            sx={{
              position: "relative",
              width: { xs: "100%", sm: "100%", md: 220 },
              height: { xs: "auto", sm: "auto", md: 220 },
              aspectRatio: { xs: "1 / 1", sm: "1 / 1", md: "auto" },
              mx: "auto",
              borderRadius: { xs: 0, md: 3 },
              overflow: "hidden",
            }}
          >
            <Avatar
              alt={artist.name}
              src={currentSrc}
              imgProps={{
                loading: priority ? "eager" : "lazy",
                decoding: "async",
                fetchPriority: priority ? "high" : "auto",
                onError: handleImageError,
              }}
              className="artist-avatar"
              sx={{
                width: "100%",
                height: "100%",
                borderRadius: 0,
                transition: "transform 0.3s ease",
              }}
            />

            {/* MOBILE NAME BADGE */}
            <Box
              sx={{
                position: "absolute",
                left: 10,
                right: 10,
                bottom: 5,
                display: { xs: "flex", md: "none" },
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  px: 1,
                  py: 0,
                  borderRadius: 999,
                  backgroundColor: "rgba(255,255,255,0.8)",
                  boxShadow: "0 6px 16px rgba(0,0,0,0.12)",
                  maxWidth: "100%",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    fontSize: 12,
                    color: "text.primary",
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 2,
                    overflow: "hidden",
                    textAlign: "center",
                  }}
                >
                  {artist.name}
                </Typography>
              </Box>
            </Box>

            {/* IG OVERLAY — IMAGE ONLY */}
            <Box
              className="ig-overlay"
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background:
                  "linear-gradient(135deg, rgba(88,81,219,0.65), rgba(193,53,132,0.65))",
                opacity: 0,
                transition: "opacity 0.25s ease",
              }}
            >
              <Typography
                sx={{
                  color: "#fff",
                  fontWeight: 600,
                  letterSpacing: "0.04em",
                }}
              >
                View items
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* TEXT */}
        <CardContent
          sx={{
            textAlign: "center",
            pt: 1,
            display: { xs: "none", md: "block" },
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {artist.name}
          </Typography>

          {artist.blurb && (
            <Typography
              variant="body2"
              sx={{
                mt: 1,
                color: "text.secondary",
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 3,
                overflow: "hidden",
              }}
            >
              {artist.blurb}
            </Typography>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
};



export default ArtistAlley;
